import React, { useEffect, useState } from 'react';
import { ExternalLink, ImageIcon, Star, X } from 'lucide-react';
import { getRestaurantReviews } from '../../../lib/api/restaurant.apis';
import ReviewForm from './ReviewForm';
import type { Review } from '../../Manager/restaurant.types';
import type { Restaurant } from '../../Manager/restaurant.types';
import RenderStars from '../Components/RenderStars';
import { useAuth } from '../../../Context/AuthContext';
import assets from '../../../assets/assets';

interface RestaurantReviewsProps {
  restaurant: Restaurant;
}


function timeAgo(dateString: string | Date): string {
  const now = new Date();
  const date = new Date(dateString);
  const diff = now.getTime() - date.getTime(); // difference in ms

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (seconds < 60) return "just now";
  if (minutes < 60) return `${minutes} min${minutes > 1 ? "s" : ""} ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
  if (weeks < 5) return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
  if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
  return `${years} year${years > 1 ? "s" : ""} ago`;
}


export default function RestaurantReviews({ restaurant }: RestaurantReviewsProps) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const { openAuthModal, user, loading: authLoading } = useAuth();

  useEffect(() => {
    setIsLoadingReviews(true);
    const fetchReviews = async () => {
      try {
        const data = await getRestaurantReviews(restaurant._id);
        setReviews(data);
      } catch (error) {
        // console.log("Failed to fetch reviews!", error);
      } finally {
        setIsLoadingReviews(false);
      }
    }
    fetchReviews();
  }, [restaurant._id]);

  const displayedReviews = reviews.slice(0, 4);
  const hasMoreReviews = reviews.length > 6;

  const truncateReview = (text, wordLimit = 25) => {
    const words = text.split(' ');
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(' ') + '...';
  };

  const metadataRating = typeof restaurant.googleMetadata?.rating === "number"
    ? restaurant.googleMetadata.rating
    : null;
  const metadataReviewCount = typeof restaurant.googleMetadata?.user_rating_count === "number"
    ? restaurant.googleMetadata.user_rating_count
    : null;
  const overallRating = metadataRating ?? restaurant?.rating?.average ?? 0;
  const overallReviewCount = metadataReviewCount ?? restaurant?.rating?.count ?? reviews.length;
  const googleMapsUrl = restaurant.googleMetadata?.google_maps_url ?? null;
  const googlePhotos = restaurant.googleMetadata?.photos ?? [];

  const handleShowMoreReviews = async () => {
    setIsLoadingReviews(true);
    // Simulate loading more reviews
    await new Promise(resolve => setTimeout(resolve, 1000));
    setShowAllReviews(true);
    setIsLoadingReviews(false);
  };
  
  // Circle Spinner Component
  const CircleSpinner = ({ size = 20, color = 'currentColor' }) => (
    <div className="flex items-center justify-center">
      <div 
        className="animate-spin rounded-full border-2 border-solid border-current border-r-transparent"
        style={{ 
          width: size, 
          height: size,
          borderColor: color,
          borderRightColor: 'transparent'
        }}
      />
    </div>
  );

  const getFirstTwoOrOneChars = (name: string) => {
    if (!name) return '??';
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleReviewModalToggle = () => {
    if (authLoading) return;

    if (!user) {
      openAuthModal();
      return;
    }

    setShowReviewForm(prev => !prev);
  };

  return (
    <div className="max-w-7xl mx-auto pt-6 lg:p-8 bg-white sm:rounded-lg border-t border-gray-300 mt-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 lg:mb-6 lg:gap-4">
        <h2 className="text-2xl font-semibold text-gray-900">Customer Reviews</h2>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <a href="#" className="text-sm text-gray-600 hover:underline">
            All reviews ({reviews.length})
          </a>
          <button
            onClick={handleReviewModalToggle}
            className="px-5 py-2 text-sm font-medium text-white bg-[#ff4e1d] rounded-full hover:bg-[#e53e1a] transition flex items-center gap-2"
            disabled={isSubmitting}
          >
            {showReviewForm ? "Cancel" : "Write a review"}
          </button>
        </div>
      </div>

      {/* New Review Form as Popup */}
      <ReviewForm
        showReviewForm={showReviewForm}
        setShowReviewForm={setShowReviewForm}
        restaurantId={restaurant._id}
      />

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column: Overall Rating + Breakdown */}
        <div className="space-y-3">

          {/* ── Google card ── */}
          {(metadataRating !== null || metadataReviewCount !== null || googleMapsUrl) && (
            <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
              {/* Logo + open link */}
              <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-gray-100">
                <img src={assets.googleLogo} alt="Google" className="h-[18px] w-auto" />
                {googleMapsUrl && (
                  <a
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open on Google Maps"
                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-[11px] font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                  >
                    Open
                    <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                )}
              </div>

              {/* Score body */}
              <div className="px-3.5 py-3.5">
                <div className="flex items-end gap-2 mb-1.5">
                  <span className="text-4xl font-semibold tracking-tight text-gray-900 leading-none">
                    {overallRating.toFixed(1)}
                  </span>
                  <div className="pb-0.5">
                    <div className="flex gap-0.5 mb-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star
                          key={i}
                          className={`w-3 h-3 ${
                            i <= Math.round(overallRating)
                              ? 'fill-[#ff4e1d] text-[#ff4e1d]'
                              : 'fill-none text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    {overallReviewCount > 0 && (
                      <span className="text-[11px] text-gray-400">
                        {overallReviewCount.toLocaleString()} reviews
                      </span>
                    )}
                  </div>
                </div>

                {googlePhotos.length > 0 && (
                  <div className="flex items-center gap-1.5 pt-2.5 border-t border-gray-100">
                    <ImageIcon className="w-3 h-3 text-gray-400" />
                    <span className="text-[11px] text-gray-400">
                      {googlePhotos.length.toLocaleString()} photo references
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── overall score card ── */}
          {overallRating > 0 && (
            <div className="rounded-xl border border-gray-100 bg-white px-3.5 py-3.5 border-l-[3px] border-l-[#ff4e1d]">
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-widest mb-2">
                Overall rating
              </p>
              <div className="flex items-end gap-2">
                <span className="text-[40px] font-semibold tracking-tight text-gray-900 leading-none">
                  {overallRating.toFixed(1)}
                </span>
                <div className="pb-0.5">
                  <div className="flex gap-0.5 mb-1">
                    <RenderStars rating={overallRating} size={15} />
                  </div>
                  {overallReviewCount > 0 && (
                    <span className="text-[11px] text-gray-400">
                      {overallReviewCount.toLocaleString()} reviews
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {!isLoadingReviews && reviews && reviews.length > 0 ? (
          <div>
            {/* Header */}
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Recent Reviews
            </h3>

            {/* Reviews List */}
            <div className="flex lg:grid gap-4 overflow-x-auto lg:overflow-visible pb-4 lg:pb-0 snap-x snap-mandatory lg:snap-none grid-cols-1 lg:grid-cols-2 lg:flex-none">
              {displayedReviews.length > 0 ? (
                displayedReviews.map((review) => {
                  const isLongReview = review.comment.split(" ").length > 25;
                  const displayText = isLongReview ? truncateReview(review.comment) : review.comment;

                  return (
                    <div
                      key={review._id}
                      className="flex-shrink-0 w-80 lg:w-full p-4 bg-gray-50 rounded-lg border border-gray-200 snap-start"
                    >
                      {/* Avatar, name, rating */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#ff4e1d] text-white flex items-center justify-center font-semibold text-sm flex-shrink-0">
                            {getFirstTwoOrOneChars(review.uid?.name || "Anonymous")}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 text-sm">
                              {review.uid?.name || "Anonymous"}
                            </h4>
                            <div className="flex gap-0.5">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-4 h-4 ${
                                    i < review.rating ? "fill-[#ff4e1d] text-[#ff4e1d]" : "text-gray-300"
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {timeAgo(review.createdAt) || ""}
                        </span>
                      </div>

                      {/* Comment */}
                      <div className="text-sm text-gray-700 leading-relaxed">
                        {displayText}
                        {isLongReview && (
                          <button
                            onClick={() => setShowAllReviews(true)}
                            className="underline text-gray-700 hover:text-gray-800 ml-1 font-medium"
                          >
                            Show more
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p>No review yet</p>
              )}
            </div>

            {/* Show More Button */}
            {hasMoreReviews && (
              <div className="mt-6 text-center">
                <button
                  onClick={handleShowMoreReviews}
                  disabled={isLoadingReviews}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium text-gray-700 flex items-center gap-2 justify-center disabled:opacity-50 disabled:cursor-not-allowed mx-auto"
                >
                  {isLoadingReviews ? (
                    <>
                      <CircleSpinner size={16} color="#6b7280" />
                      Loading...
                    </>
                  ) : (
                    "Show more reviews"
                  )}
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-600 hover:text-gray-800 hover:cursor-pointer hover:underline" onClick={handleReviewModalToggle}>Share your experience with this place!</p>
        )}
      </div>

      {/* All Reviews Popup */}
      {showAllReviews && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center lg:p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">All Reviews ({reviews.length})</h3>
              <button
                onClick={() => setShowAllReviews(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Reviews List */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid gap-6">
                {reviews.map((review) => (
                  <div
                    key={review._id}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {/* Avatar */}
                        <div className="w-10 h-10 rounded-full bg-[#ff4e1d] text-white flex items-center justify-center font-semibold text-sm flex-shrink-0">
                          {getFirstTwoOrOneChars(review.uid?.name || 'Anonymous')}
                        </div>

                        <div>
                          {/* Name */}
                          <h4 className="font-semibold text-gray-900 text-sm">{review.uid?.name || 'Anonymous'}</h4>
                          {/* Rating */}
                          <div className="flex gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-4 h-4 ${
                                  i < review.rating ? 'fill-[#ff4e1d] text-[#ff4e1d]' : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Date */}
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {new Date(review.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="text-sm text-gray-700 leading-relaxed">
                      {review.comment}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowAllReviews(false)}
                className="w-full py-3 bg-[#ff4e1d] text-white rounded-lg hover:bg-[#e53e1a] transition font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}